import { Button } from "@/components/ui/baseUi/button"
import backgroundSemiFinal from "@images/backgroundSemiFinal.png"
import { HUPortableText } from "@modules/common/cms/components/huPortableText"
import { word } from "@modules/core/utils/i18n"
import { CrossFitEvent } from "@modules/crossfitEvent/entities/crossfitEventEntity"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { YouTubeVideoPlayer } from "@modules/utils/components/youTubeVideo"
import styled from "styled-components"
import { Text } from "@/components/ui/baseUi/text"

type CrossfitEventCardProps = {
  crossfitEvent: CrossFitEvent
} & React.ComponentProps<"div">

export function CrossfitEventDetailsInfo({ crossfitEvent }: CrossfitEventCardProps) {
  const openWebsite = (link: string) => {
    window.open(link, "_blank")
  }

  return (
    <>
      {crossfitEvent ? (
        <div className="flex grow flex-col">
          <HeaderContainer>
            <LogoSemiFinalCompetition $logoSemiFinal={crossfitEvent.logo?.uri || ""} />
            <div className="relative flex justify-center">
              <CrossFitTag>
                <Text fontStyle="LL" className="text-textDefault-body">
                  {word(`semiFinals.crossfitSemiFinal.tag`)}
                </Text>
              </CrossFitTag>
              <BackgroundContainer
                $bgImage={backgroundSemiFinal}
                className="mb-8 flex-row items-center justify-center gap-2 overflow-hidden rounded-t-none p-8"
              >
                <TitleContainer>
                  <div className="flex gap-2">
                    <Text fontStyle="TL" className="text-textDefault-body">
                      {crossfitEvent.subtitle}
                    </Text>
                  </div>
                </TitleContainer>
              </BackgroundContainer>
            </div>
          </HeaderContainer>
          <InfoContainer>
            <TitleCompetition fontStyle="TM" className="text-textDefault-body">
              {crossfitEvent.title}
            </TitleCompetition>
            <Line>
              <Text fontStyle="LL" className="mr-2 text-textDefault-caption">
                {`${word("sessionForm.startTime.title")} : `}
              </Text>
              <Text fontStyle="BL" className="text-textDefault-body">
                {crossfitEvent.date.start}
              </Text>
            </Line>
            <Line>
              <Text fontStyle="LL" className="mr-2 text-textDefault-caption">
                {`${word("semiFinals.crossfitSemiFinal.ended")} : `}
              </Text>
              <Text fontStyle="BL" className="text-textDefault-body">
                {crossfitEvent.date.end}
              </Text>
            </Line>
            <Line>
              <Text fontStyle="LL" className="mr-2 text-textDefault-caption">
                {`${word("semiFinals.crossfitSemiFinal.place")} : `}
              </Text>
              <Text fontStyle="BL" className="text-textDefault-body">
                {crossfitEvent.place}
              </Text>
            </Line>
            <Line className="mt-4 justify-between px-12">
              <Button variant="outline" onClick={() => openWebsite(crossfitEvent.cta?.tickets ?? "")}>
                {word("semiFinals.crossfitSemiFinal.getTickets")}
              </Button>
              <Button variant="outline" onClick={() => openWebsite(crossfitEvent.cta?.website ?? "")}>
                {word("semiFinals.crossfitSemiFinal.website")}
              </Button>
            </Line>
          </InfoContainer>
          <ContentContainer>
            <HUPortableText value={crossfitEvent.description || []} />
            {crossfitEvent.watchLive && (
              <div className="mt-2">
                <YouTubeVideoPlayer link={crossfitEvent.watchLive[0]?.url ?? ""} width={540} />
              </div>
            )}
          </ContentContainer>
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </>
  )
}

const HeaderContainer = styled.div`
  margin: -85px -20px;
  position: relative;
`

const BackgroundContainer = styled.div<{ $bgImage?: string }>`
  position: relative;
  border-radius: 33px;
  border-width: 0px;
  height: 370px;
  background-image: ${({ $bgImage }) => `url(${$bgImage})`};
  background-size: cover;
  background-position: center;
  width: 100%;
`

const TitleContainer = styled.div`
  position: absolute;
  bottom: 30px;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px 30px;
`
const CrossFitTag = styled.div`
  position: absolute;
  bottom: 95px;
  left: 20px;
  border-radius: 44px;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(7px);
  padding: 9px 12px 8px 12px;
  z-index: 2;
`

const ContentContainer = styled.div`
  margin-top: 20px;
  text-align: justify;
`
const InfoContainer = styled.div`
  margin-top: 70px;
`
const Line = styled.div`
  display: flex;
  align-items: baseline;
`
const TitleCompetition = styled(Text)`
  margin-bottom: 20px;
`

const LogoSemiFinalCompetition = styled.div<{
  $logoSemiFinal: string
  $heightLogo?: number
  $widthLogo?: number
  $rightSize?: number
  $topSize?: number
}>`
  position: absolute;
  top: ${({ $topSize }) => ($topSize ? `${$topSize}%` : "10%")};
  right: ${({ $rightSize }) => ($rightSize ? `${$rightSize}%` : "15%")};
  background-image: ${({ $logoSemiFinal }) => `url(${$logoSemiFinal})`};
  background-size: contain;
  background-position: center;
  height: ${({ $heightLogo }) => ($heightLogo ? `${$heightLogo}%` : "67%")};
  width: ${({ $widthLogo }) => ($widthLogo ? `${$widthLogo}%` : "62%")};
  background-repeat: no-repeat;
  z-index: 3;
`
